import { useState, useEffect } from "react";
import FormInput from "../Input";
import DefaultButton from "../Button/Button";
import { endpoints, postFunc } from "../../api";
import { residentPwaLink } from "../../constants";

interface WaitlistModalProps{
    open: boolean;
    onClose: () => void
}

const WaitlistModal = ({ open, onClose }: WaitlistModalProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [error, setError] = useState<string | null>(null);

  // Reset state whenever modal opens
  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "" });
      setError(null);
      setSubmitted(false);
      setLoading(false);
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (loading) return;

    setError(null);
    if (!form.email || !form.name) {
      setError("Both name and email are required");
      return;
    }

    setLoading(true);
    try {
      await postFunc(endpoints.waitlist, form);
      setSubmitted(true);
    } catch (e: any) {
      if (e.statusCode === 409 && e.message) {
        setError(e.message); 
      } else {
        setError(e.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg w-full max-w-md text-center shadow-lg">
        <h2 className="text-2xl font-semibold mb-2">🚧 Coming Soon</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Safelto App is not yet available. Join the waitlist to be notified when we launch.
        </p>

        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-dark-900">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            Residents can already use Safelto in the browser to create and share guest codes.
          </p>
          <a
            href={residentPwaLink()}
            className="inline-flex w-full items-center justify-center rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-black dark:hover:bg-gray-300"
          >
            Open Safelto Resident
          </a>
        </div>

        {!submitted ? (
          <div className="flex flex-col gap-4">
            <FormInput
              name="name"
              label="Name"
              placeholder="Enter your full name"
              value={form.name}
              onChange={handleChange}
            />
            <FormInput
              name="email"
              label="Email"
              placeholder="Enter your email"
              value={form.email}
              onChange={handleChange}
            />

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <DefaultButton
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Submitting..."
              disabled={loading}
              className="w-full py-2 rounded"
            >
              Join Waitlist
            </DefaultButton>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <p className="text-green-500 text-lg font-medium">🎉 You’re on the list!</p>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You’ll be the first to know once we launch.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 mt-6">
          <DefaultButton
          buttonType='secondary'
          onClick={() => (window.location.href = "/about")}
          >
            Explore Other Features
          </DefaultButton>
          <button
            onClick={onClose}
            className="w-full py-2 text-gray-500 hover:text-gray-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaitlistModal;
